import { useEffect, useState } from "react";
import axios from "axios";
import Logo from "@/components/Logo";
import { LogOut, Loader2, Car, Euro, CheckCircle2, Clock, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pd_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const loadBookings = async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    }
  };

  useEffect(() => {
    if (token) loadBookings(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const inputCls = "gold-input w-full px-4 py-3 text-sm text-white rounded-xl placeholder-[#5a6475]";

  if (!token) {
    return (
      <div className="min-h-screen bg-[#060709] text-[#f4f4f7] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="gold-card rounded-2xl p-8 md:p-10 max-w-sm w-full">
          <div className="flex flex-col items-center mb-8">
            <Logo size={52} className="rounded-xl mb-4" />
            <h1 className="font-disp text-2xl text-white">Espace Admin</h1>
            <p className="font-mono text-[10px] text-[#c9a24d] uppercase tracking-[0.25em] mt-1">Prestige Drive</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email admin"
              className={inputCls} data-testid="admin-email-input" required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe"
              className={inputCls} data-testid="admin-password-input" required />
            {error && <p className="text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-button"
              className="w-full py-3.5 bg-gradient-to-r from-[#c9a24d] via-[#d9b86c] to-[#a17c2e] hover:brightness-110 text-[#060709] font-bold font-geom text-xs uppercase tracking-[0.2em] rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060709] text-[#f4f4f7]" data-testid="admin-dashboard">
      <nav className="border-b border-[#212631] px-6 py-4 bg-[#060709]/90 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={34} className="rounded-lg" />
            <span className="font-geom text-sm font-bold tracking-[0.2em] text-white">TABLEAU DES COURSES</span>
          </div>
          <button onClick={logout} data-testid="admin-logout-button"
            className="flex items-center gap-2 font-mono text-xs text-[#8c98a9] hover:text-[#c9a24d] transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!data ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="w-8 h-8 text-[#c9a24d] animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {[
                { icon: Car, label: "Réservations", value: data.stats.total, tid: "stat-total" },
                { icon: CheckCircle2, label: "Payées", value: data.stats.paid, tid: "stat-paid" },
                { icon: Clock, label: "En attente", value: data.stats.pending, tid: "stat-pending" },
                { icon: Euro, label: "Revenus encaissés", value: `${data.stats.revenue_eur.toFixed(2)} €`, tid: "stat-revenue" },
              ].map((s) => (
                <div key={s.tid} data-testid={s.tid}
                  className="bg-[#0d1017] border border-[#212631] rounded-xl p-5 hover:border-[#c9a24d]/40 transition-colors">
                  <s.icon className="w-5 h-5 text-[#c9a24d] mb-2" />
                  <div className="font-disp text-2xl text-white">{s.value}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8c98a9] mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-[#0d1017] border border-[#212631] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bookings-table">
                  <thead>
                    <tr className="border-b border-[#212631] font-mono text-[10px] uppercase tracking-widest text-[#8c98a9] text-left">
                      <th className="px-5 py-4">Référence</th>
                      <th className="px-5 py-4">Client</th>
                      <th className="px-5 py-4">Trajet</th>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Véhicule</th>
                      <th className="px-5 py-4 text-right">Prix</th>
                      <th className="px-5 py-4 text-right">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bookings.length === 0 && (
                      <tr><td colSpan="7" className="px-5 py-10 text-center text-[#8c98a9] text-xs font-mono" data-testid="bookings-empty">
                        Aucune réservation pour le moment
                      </td></tr>
                    )}
                    {data.bookings.map((b) => (
                      <tr key={b.id} className="border-b border-[#161a23] hover:bg-[#10141d] transition-colors" data-testid={`booking-row-${b.reference}`}>
                        <td className="px-5 py-4 font-mono text-xs text-[#c9a24d]">{b.reference}</td>
                        <td className="px-5 py-4">
                          <div className="text-white text-xs font-semibold">{b.name}</div>
                          <div className="text-[#8c98a9] text-[11px]">{b.email} · {b.phone}</div>
                        </td>
                        <td className="px-5 py-4 text-xs text-[#c6cedb] max-w-[220px]">
                          <span className="flex items-start gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-[#c9a24d] mt-0.5 shrink-0" />
                            <span className="truncate">{b.pickup} → {b.destination}</span>
                          </span>
                          <span className="text-[10px] text-[#8c98a9] font-mono">{b.distance_km} km</span>
                        </td>
                        <td className="px-5 py-4 font-mono text-xs text-[#c6cedb] whitespace-nowrap">{b.date}<br/>{b.time}</td>
                        <td className="px-5 py-4 text-xs text-[#c6cedb]">{b.vehicle_label.split("—")[0]}</td>
                        <td className="px-5 py-4 text-right font-disp text-base text-white whitespace-nowrap">{b.price_eur.toFixed(2)} €</td>
                        <td className="px-5 py-4 text-right">
                          <div className="font-mono text-[10px] uppercase tracking-wider text-[#8c98a9] mb-1" data-testid={`booking-method-${b.reference}`}>
                            {{stripe: "Carte en ligne", especes: "Espèces", devis: "Devis"}[b.payment_method || "stripe"]}
                          </div>
                          {b.payment_status === "paid" ? (
                            <span className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#34d399]/10 text-[#34d399] border border-[#34d399]/30">Payée</span>
                          ) : b.payment_status === "especes_a_regler" ? (
                            <span className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#c9a24d]/10 text-[#c9a24d] border border-[#c9a24d]/30">À régler au chauffeur</span>
                          ) : b.payment_status === "devis" ? (
                            <span className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#7ea3d4]/10 text-[#7ea3d4] border border-[#7ea3d4]/30">Devis demandé</span>
                          ) : (
                            <span className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#c9a24d]/10 text-[#c9a24d] border border-[#c9a24d]/30">En attente</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
